/**
 * One agent's conversation inside the panel: the team channel for a
 * practice or client scope. Mounted per scope (keyed by the parent) so thread
 * state resets when the user switches agent or client. The chat itself is
 * `useScopedAgentChat`; this file is the panel's layout around it.
 */
import { useCallback, useEffect, useRef, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";

import { Suggestion } from "@/components/ai-elements/suggestion";
import { Button } from "@/components/ui/button";
import {
  joinedOf,
  memberOf,
  personLabel
} from "@/features/caseIde/chat/components/ThreadMenuItem";
import { ThreadSwitcher } from "@/features/caseIde/chat/components/ThreadSwitcher";
import { cn } from "@/lib/utils";

import { useAgentChatHost } from "../agentChatHostStore";
import {
  panelAgentLabel,
  useAgentPanelStore,
  type PanelAgent
} from "../agentPanelStore";
import {
  elsewherePath,
  useElsewhereThreads
} from "../hooks/useElsewhereThreads";
import type { AttachmentFilePart } from "../attachments/types";
import { relativeTime } from "../lib/time";
import { OPEN_THREAD_STATE } from "../routes";
import {
  continuityCheck,
  isStale,
  type ContinuityReason
} from "../threadContinuity";
import { focusSubtitle, type AgentScope, type AgentSpec } from "../types";
import type { AgentMessageMetadata } from "../useAgentChat";
import { useScopedAgentChat } from "../useScopedAgentChat";
import { AgentComposerBar } from "./AgentComposerBar";
import { AgentPanelHeader, type ThreadProvenance } from "./AgentPanelHeader";
import { AgentTranscript } from "./AgentTranscript";
import { ContinuityPrompt } from "./ContinuityPrompt";

export const AGENT_PANEL_OWNER = "agent-panel";

export { DISCLAIMER } from "./AgentComposerBar";

/** The transcript and the composer share one centered column, at most 680px. */
const COLUMN = "mx-auto w-full max-w-170";

type Props = {
  agent: PanelAgent;
  scope: AgentScope;
  spec: AgentSpec | null;
  /** The panel is at its wide width (the header offers Narrow). */
  wide?: boolean;
  /** Jumps the panel between narrow and wide; omitted when the window is too small. */
  onToggleWidth?: () => void;
  /** The panel fills the page (the header offers Restore Chat Size). */
  expanded?: boolean;
  /** Expands the panel to the page, or restores it. */
  onToggleExpand?: () => void;
};

export const AgentPanelChat: FC<Props> = ({
  agent,
  scope,
  spec,
  wide,
  onToggleWidth,
  expanded,
  onToggleExpand
}) => {
  const navigate = useNavigate();
  const consumePendingThread = useAgentPanelStore(
    (s) => s.consumePendingThread
  );
  const setOpen = useAgentPanelStore((s) => s.setOpen);

  // A deep link (`?agent=&thread=&message=`) for this agent. The one present
  // at mount seeds the chat's thread; a later one (panel already open)
  // switches threads. Consumed in an effect so render stays pure.
  const pending = useAgentPanelStore((s) =>
    s.pendingThread?.agent === agent ? s.pendingThread : null
  );
  const initialPending = useRef(pending).current;
  const [linkedMessageId, setLinkedMessageId] = useState<string | null>(
    initialPending?.messageId ?? null
  );

  // The page's own wiring (the client profile's Requests), when it has any.
  const host = useAgentChatHost(scope);

  const scoped = useScopedAgentChat({
    agent,
    scope,
    spec,
    owner: AGENT_PANEL_OWNER,
    initialThreadId: initialPending?.threadId ?? null,
    linkedMessageId,
    adapters: host?.adapters
  });
  const {
    chat,
    me,
    teamMembers,
    activeThread,
    firstName,
    greeting,
    starters,
    unreadByThread,
    empty,
    send,
    takeResearchMetadata
  } = scoped;
  const people = { members: teamMembers, meUid: me.uid || null };
  const starterLabel = activeThread
    ? personLabel(people, activeThread.created_by)
    : null;
  const provenance: ThreadProvenance | null =
    activeThread && !empty && starterLabel
      ? {
          starter: memberOf(people, activeThread.created_by),
          starterLabel: starterLabel === "You" ? "you" : starterLabel,
          age: relativeTime(activeThread.created_at),
          joined: joinedOf(activeThread, people)
        }
      : null;
  const { setThreadId } = chat;
  useEffect(() => {
    if (!pending) return;
    if (pending !== initialPending) setThreadId(pending.threadId);
    setLinkedMessageId(pending.messageId);
    consumePendingThread(agent);
  }, [pending, initialPending, setThreadId, consumePendingThread, agent]);

  // A dashboard composer's message: the first of a new conversation. The
  // chat leaves whatever thread it is on, then the send creates the thread,
  // so nothing waits on a restore that a fresh thread never runs.
  const draft = useAgentPanelStore((s) =>
    s.pendingDraft?.agent === agent ? s.pendingDraft : null
  );
  const consumePendingDraft = useAgentPanelStore((s) => s.consumePendingDraft);
  const { threadId, threadsLoading } = chat;
  useEffect(() => {
    if (!draft || threadsLoading) return;
    if (threadId !== null) {
      setThreadId(null);
      return;
    }
    const taken = consumePendingDraft(agent);
    if (taken) send(taken.text, undefined, taken.metadata);
  }, [
    draft,
    threadsLoading,
    threadId,
    setThreadId,
    consumePendingDraft,
    agent,
    send
  ]);

  // Reopening the panel on a thread nobody has touched for hours starts a
  // fresh conversation instead, with a link back to the old one. Checked once,
  // when the threads first load; a deep link is always honoured.
  const [continueFrom, setContinueFrom] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const staleChecked = useRef(false);
  useEffect(() => {
    if (staleChecked.current || threadsLoading) return;
    staleChecked.current = true;
    if (initialPending || draft || !activeThread) return;
    if (activeThread.kind === "channel" || activeThread.kind === "updates")
      return;
    if (!isStale(activeThread)) return;
    setContinueFrom({ id: activeThread.id, title: activeThread.title });
    setThreadId(null);
  }, [threadsLoading, initialPending, draft, activeThread, setThreadId]);

  // A message for an old or unrelated thread waits while the panel asks
  // whether it belongs in a new chat; "Send Here" for a thread is not asked
  // again this session.
  const [held, setHeld] = useState<{
    text: string;
    files?: AttachmentFilePart[];
    reason: ContinuityReason;
    title: string;
  } | null>(null);
  const confirmedHere = useRef(new Set<string>());
  const onBeforeSend = useCallback(
    (text: string, files?: AttachmentFilePart[]) => {
      if (!activeThread || confirmedHere.current.has(activeThread.id))
        return true;
      const reason = continuityCheck(activeThread, text);
      if (!reason) return true;
      setHeld({ text, files, reason, title: activeThread.title });
      return false;
    },
    [activeThread]
  );
  const [freshSend, setFreshSend] = useState<{
    text: string;
    files?: AttachmentFilePart[];
    metadata?: AgentMessageMetadata;
  } | null>(null);
  useEffect(() => {
    if (!freshSend || threadId !== null) return;
    setFreshSend(null);
    send(freshSend.text, freshSend.files, freshSend.metadata);
  }, [freshSend, threadId, send]);
  const sendInNewChat = useCallback(() => {
    if (!held) return;
    setHeld(null);
    setContinueFrom(null);
    chat.startNewThread();
    setFreshSend({
      text: held.text,
      files: held.files,
      metadata: takeResearchMetadata()
    });
  }, [held, chat, takeResearchMetadata]);
  const sendHere = useCallback(() => {
    if (!held) return;
    if (threadId) confirmedHere.current.add(threadId);
    setHeld(null);
    send(held.text, held.files, takeResearchMetadata());
  }, [held, threadId, send, takeResearchMetadata]);
  useEffect(() => {
    setHeld(null);
  }, [threadId]);

  const turnRunning =
    chat.status === "submitted" || chat.status === "streaming";
  useEffect(() => {
    if (!turnRunning) host?.onTurnIdle?.();
  }, [turnRunning, host]);

  // The host's prompts: sent, or seeded into the composer.
  const promptListeners = useRef(new Set<(text: string) => void>());
  useEffect(
    () =>
      host?.onPromptRequest((text, opts) => {
        if (opts.send) send(text);
        else promptListeners.current.forEach((fn) => fn(text));
      }),
    [host, send]
  );
  const onPromptRequest = useCallback((register: (text: string) => void) => {
    promptListeners.current.add(register);
    return () => {
      promptListeners.current.delete(register);
    };
  }, []);

  const elsewhere = useElsewhereThreads(scope, chat.threads.length);

  return (
    <div
      className="flex h-full min-h-0 flex-col"
      data-testid={`agent-panel-chat-${agent}`}
    >
      <AgentPanelHeader
        label={panelAgentLabel(agent)}
        subtitle={
          activeThread?.visibility === "private"
            ? "Only You"
            : activeThread?.kind === "updates"
            ? "Updates"
            : focusSubtitle(scope.kind)
        }
        onNew={chat.threadsLoading ? undefined : chat.startNewThread}
        onToggleWidth={onToggleWidth}
        wide={wide}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        provenance={provenance}
        menu={
          <ThreadSwitcher
            compact
            triggerSize="icon-xs"
            threads={chat.threads}
            activeId={chat.threadId}
            onSelect={chat.setThreadId}
            onNew={chat.startNewThread}
            onDelete={(id) => void chat.deleteThread(id)}
            disabled={chat.threadsLoading}
            unreadByThread={unreadByThread}
            meUid={me.uid || null}
            members={teamMembers}
            elsewhere={elsewhere}
            onOpenElsewhere={(t) =>
              navigate(elsewherePath(t), { state: OPEN_THREAD_STATE })
            }
          />
        }
      />

      {empty ? (
        <div
          className={cn(
            COLUMN,
            "flex min-h-0 flex-1 flex-col justify-end gap-1.5 px-4 pb-4"
          )}
          data-testid="agent-panel-greeting"
        >
          <h2 className="text-lg font-semibold tracking-tight">
            Hello{firstName ? `, ${firstName}` : ""}
          </h2>
          <p className="text-sm text-muted-foreground">{greeting}</p>
          {continueFrom ? (
            <Button
              variant="link"
              size="xs"
              className="self-start text-sm text-fg-3"
              onClick={() => {
                setThreadId(continueFrom.id);
                setContinueFrom(null);
              }}
              data-testid="continue-thread"
            >
              Continue {continueFrom.title || "the last conversation"}
            </Button>
          ) : null}
        </div>
      ) : (
        <AgentTranscript scoped={scoped} contentClassName={COLUMN} />
      )}

      <AgentComposerBar
        scoped={scoped}
        showOnScreen
        className={cn(COLUMN, "px-3 pb-2")}
        onPromptRequest={onPromptRequest}
        onBeforeSend={onBeforeSend}
        above={
          held ? (
            <ContinuityPrompt
              title={held.title}
              reason={held.reason}
              onNewChat={sendInNewChat}
              onSendHere={sendHere}
            />
          ) : empty && starters.length ? (
            <div
              className="flex flex-col items-start gap-2 pb-1"
              data-testid="agent-panel-starters"
            >
              {starters.map((s) => (
                <Suggestion
                  key={s}
                  suggestion={s}
                  onClick={send}
                  className="h-auto min-h-8 max-w-full justify-start whitespace-normal rounded-2xl px-3 py-1.5 text-left text-xs leading-snug font-normal"
                />
              ))}
            </div>
          ) : null
        }
      />
    </div>
  );
};
